import {AxiosResponse} from 'axios'
import {ListPhotoPersonTagsRequest, PhotoPersonTagListResponse} from './types'
import {HttpInstance} from '../createHttpInstance'

export interface ListPhotoPersonTags<P, R> {
  (http: HttpInstance, params: P): Promise<AxiosResponse<R>>
}

/**
 * Every person tag on a single photo.
 *
 * Named `listPhotoPersonTags` to preserve a distinction the photos service makes
 * deliberately: this is the "all tags on one photo" query, while the
 * pre-existing `listPhotoTags` is the INVERSE — all photos for one person. The
 * two routes differ only in shape (`/photos/{photoid}/person-tags` vs
 * `/photos/person-tags/{personid}`), so collapsing the names would make the
 * wrong one easy to reach for.
 *
 * An untagged photo is a 200 with `{"Results":[]}`, not a 404 — that empty
 * result is the whole point of the endpoint. `Results` is guaranteed non-null.
 * A `clientid` or `photoid` below 1 is a 400.
 */
export const listPhotoPersonTags: ListPhotoPersonTags<
  ListPhotoPersonTagsRequest,
  PhotoPersonTagListResponse
> = (http, params) => {
  const {ClientID, PhotoID} = params

  // Encoded for the same reason the query below is built rather than
  // interpolated: safety by construction, not by the id happening to be numeric.
  // Byte-identical for numeric ids, so the route contract test is unaffected.
  const path = '/v1/photos/{photoid}/person-tags'.replace(
    '{photoid}',
    encodeURIComponent(PhotoID),
  )

  // Built rather than interpolated: a caller widening the query by smuggling
  // a second parameter through the id should be impossible by construction
  // rather than by the current type happening to be numeric. The emitted
  // string is byte-identical for numeric ids, so the route contract test is
  // unaffected.
  const query = new URLSearchParams({clientid: String(ClientID)})

  return http.request<PhotoPersonTagListResponse>({
    method: 'GET',
    url: `${path}?${query.toString()}`,
  })
}
